import { Autocomplete, TextField } from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";
import { useEffect, useRef } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomerOrgCompanyAutocompleteSingle from "../../../../../common/components/functional/autocomplete/customerOrgCompanyAutocomplete/CustomerOrgCompanyAutocompleteSingle";
import useCustomerOrgCompanyAutocomplete from "../../../../../common/components/functional/autocomplete/customerOrgCompanyAutocomplete/useCustomerOrgCompanyAutocomplete";
import DialogComponent from "../../../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../../../common/components/functional/dialogs/DialogContentWrapper";
import { WorkLocationAutocompleteFieldMulti } from "../../../../../common/components/functional/fields/workLocationAutocompleteField/WorkLocationAutocompleteFieldMulti";
import WorkTitleAutocompleteField from "../../../../../common/components/functional/fields/WorkTitleAutocompleteField";
import FormGridRow from "../../../../../common/components/functional/formGridRow/FormGridRow";
import isEmpty from "../../../../../common/helpers/isEmpty";
import { NameIdOption } from "../../../../../common/types/commonTypes";
import { TargetedShiftInfoText } from "./styles";

export type RuleFormValues = {
  id?: string;
  customerCompany: NameIdOption | null;
  customerOrganization: NameIdOption | null;
  workLocations: NameIdOption[];
  workTitles: NameIdOption[];
  isDefaultRule: boolean;
};

type RuleOption = NameIdOption | null;

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (values: RuleFormValues) => Promise<void>;
  initialValues?: Partial<RuleFormValues>;
  contractId?: string;
  /**
   * The customer company the parent order/employment is already tied to.
   * When provided, the customer company is fixed and only organizations and
   * work locations belonging to it can be selected.
   */
  customerCompany?: NameIdOption | null;
};

export const RuleEditDialog = ({
  open,
  onClose,
  onSave,
  initialValues,
  contractId,
  customerCompany: contextCustomerCompany,
}: Props) => {
  const { t } = useTranslation();

  const dialogTitle = initialValues
    ? t("talentProfile:targetedShifts.ruleEditTitle")
    : t("talentProfile:targetedShifts.addRule");

  const methods = useForm<RuleFormValues>({
    defaultValues: {
      id: initialValues?.id ?? nanoid(),
      customerCompany:
        initialValues?.customerCompany ?? contextCustomerCompany ?? null,
      customerOrganization: initialValues?.customerOrganization ?? null,
      workLocations: initialValues?.workLocations ?? [],
      workTitles: initialValues?.workTitles ?? [],
      isDefaultRule: initialValues?.isDefaultRule ?? false,
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = methods;

  const { customerCompany, customerOrganization, workTitles, workLocations } =
    watch();

  const isEditingExistingRule = !!initialValues;
  const saveDisabled = isEditingExistingRule ? !isDirty : !customerCompany;
  // The company is fixed to the order/employment context whenever it is known,
  // so organizations and work locations must be restricted to that company only.
  const isCustomerCompanyLocked =
    !!initialValues?.isDefaultRule || !!contextCustomerCompany;

  const {
    onChange: onCustomerOrgChange,
    customerOptions,
    customerCompanyOptions,
    onInputChange: onCustomerInputChange,
    isLoading: isCustomerLoading,
    isFixedOptions,
  } = useCustomerOrgCompanyAutocomplete({
    customerCompany: contextCustomerCompany ?? customerCompany ?? undefined,
  });

  const handleCustomerCompanyChange = (
    currentValue: RuleOption,
    newValue: RuleOption,
    onChange: (value: RuleOption) => void,
  ) => {
    onChange(newValue);
    if (currentValue?.id !== newValue?.id) {
      setValue("customerOrganization", null);
      setValue("workLocations", []);
    }
  };

  const handleCustomerOrganizationChange = (
    currentValue: RuleOption,
    newValue: RuleOption,
  ) => {
    if (currentValue?.id !== newValue?.id) {
      setValue("workLocations", []);
    }
    setValue("customerOrganization", newValue, { shouldDirty: true });
    const company = customerOptions.find((company) =>
      company.customerOrganizations.some((org) => org.id === newValue?.id),
    );

    if (company) {
      setValue("customerCompany", { id: company.id, name: company.name });
    }
    onCustomerOrgChange(newValue);
  };

  const onSubmit = async (values: RuleFormValues) => {
    await onSave(values);
  };

  // `initialValues`/`contextCustomerCompany` are recreated on every render by
  // the callers, so this effect must only reset the form when the dialog
  // actually opens (or when it starts editing a different rule while already
  // open) - not on every unrelated parent re-render, or it would silently
  // overwrite the user's in-progress edits (e.g. a newly picked organization)
  // back to the original values.
  const wasOpenRef = useRef(false);
  const resetIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentId = initialValues?.id;
    const shouldReset =
      open && (!wasOpenRef.current || resetIdRef.current !== currentId);

    if (shouldReset) {
      reset({
        id: initialValues?.id ?? nanoid(),
        customerCompany:
          initialValues?.customerCompany ?? contextCustomerCompany ?? null,
        customerOrganization: initialValues?.customerOrganization ?? null,
        workLocations: initialValues?.workLocations ?? [],
        workTitles: initialValues?.workTitles ?? [],
        isDefaultRule: initialValues?.isDefaultRule ?? false,
      });
      resetIdRef.current = currentId;
    }
    wasOpenRef.current = open;
  }, [open, initialValues, reset, contractId, contextCustomerCompany]);

  return (
    <DialogComponent
      open={open}
      onClose={onClose}
      sx={{ maxWidth: "600px" }}
      dragHandleSelector="rule-edit-dialog"
    >
      <DialogContentWrapper
        title={dialogTitle}
        onCancel={onClose}
        onSave={handleSubmit(onSubmit)}
        saveDisabled={saveDisabled}
        margin
      >
        <FormProvider {...methods}>
          <FormGridRow label={t("talentProfile:customerCompany")}>
            <Controller
              control={control}
              name="customerCompany"
              render={({ field }) => (
                <Autocomplete
                  options={customerCompanyOptions}
                  value={field.value}
                  disabled={isCustomerCompanyLocked}
                  onChange={(_, newValue: any) => {
                    handleCustomerCompanyChange(
                      field.value,
                      newValue,
                      field.onChange,
                    );
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("common:writeToSearch")}
                      size="small"
                      error={!!errors.customerCompany}
                    />
                  )}
                  onInputChange={(_, value, reason) =>
                    onCustomerInputChange(value, reason)
                  }
                  //isLoading={isCustomerLoading}
                />
              )}
            />
          </FormGridRow>
          <FormGridRow label={t("talentProfile:organization")}>
            <Controller
              control={control}
              name="customerOrganization"
              render={({ field }) => (
                <CustomerOrgCompanyAutocompleteSingle
                  options={customerOptions}
                  value={field.value}
                  onChange={(newValue) =>
                    handleCustomerOrganizationChange(field.value, newValue)
                  }
                  onInputChange={onCustomerInputChange}
                  isLoading={isCustomerLoading}
                  disabled={initialValues?.isDefaultRule}
                  useFixedOptions={isFixedOptions}
                />
              )}
            />
          </FormGridRow>
          <FormGridRow label={t("workLocation")}>
            <WorkLocationAutocompleteFieldMulti
              name="workLocations"
              customerCompanyId={customerCompany?.id}
              customerOrgId={customerOrganization?.id}
              contractId={contractId}
              groupByCustomerOrg={true}
              disabled={!customerOrganization && !customerCompany}
              useFixedOptions={!!customerOrganization}
              placeholder={
                isEmpty(workLocations)
                  ? t("talentProfile:selectWorkLocation")
                  : undefined
              }
            />
          </FormGridRow>
          <FormGridRow label={t("workTitles")} multiRow>
            <WorkTitleAutocompleteField
              name="workTitles"
              fullWidth
              fetchAll
              placeholder={
                isEmpty(workTitles)
                  ? t("talentProfile:selectWorkTitles")
                  : undefined
              }
            />
          </FormGridRow>
          <FormGridRow labelSize={0} fieldSize={12}>
            <TargetedShiftInfoText sx={{ padding: "8px 4px 0px 12px" }}>
              {t("talentProfile:targetedShifts.ruleEditInfoText")}
            </TargetedShiftInfoText>
          </FormGridRow>
        </FormProvider>
      </DialogContentWrapper>
    </DialogComponent>
  );
};
